import { useState } from "react";

import { GitMerge, Loader2, AlertCircle, CheckCircle2, Info } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogFooterWithShortcuts, CancelButton, SubmitButton } from "@/components/ui/dialog-footer-with-shortcuts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface Conflict {
  type: "memory_conflict" | "context_contradiction" | "duplicate_context";
  source_item: string;
  target_item: string;
  severity: "high" | "medium" | "low";
  description: string;
  suggestion: string;
}

interface MergeAnalysis {
  conflicts: Conflict[];
  unique_insights_source: string[];
  unique_insights_target: string[];
  merge_summary: string;
  recommended_strategy: "merge_into_existing" | "create_new_branch";
}

interface MergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceBranchName: string;
  targetBranchName: string;
  onInitiateMerge: (
    strategy: "merge_into_existing" | "create_new_branch",
    resolutions: Record<number, "source" | "target" | "both" | "neither">,
    newSessionName?: string
  ) => void;
  onAnalyze: () => Promise<MergeAnalysis>;
}

export function MergeDialog({
  open,
  onOpenChange,
  sourceBranchName,
  targetBranchName,
  onInitiateMerge,
  onAnalyze
}: MergeDialogProps) {
  const [step, setStep] = useState<"select" | "analyzing" | "review">("select");
  const [analysis, setAnalysis] = useState<MergeAnalysis | null>(null);
  const [strategy, setStrategy] = useState<"merge_into_existing" | "create_new_branch">("merge_into_existing");
  const [newSessionName, setNewSessionName] = useState("");
  const [resolutions, setResolutions] = useState<Record<number, "source" | "target" | "both" | "neither">>({});

  const handleAnalyze = async () => {
    setStep("analyzing");
    try {
      const result = await onAnalyze();
      setAnalysis(result);
      setStrategy(result.recommended_strategy);
      setStep("review");
      
      // Initialize resolutions based on AI suggestions
      const initialResolutions: Record<number, "source" | "target" | "both" | "neither"> = {};
      result.conflicts.forEach((conflict, index) => {
        // Default to "both" for duplicates, "source" for high severity conflicts
        if (conflict.type === "duplicate_context") {
          initialResolutions[index] = "source";
        } else if (conflict.severity === "high") {
          initialResolutions[index] = "source";
        } else {
          initialResolutions[index] = "both";
        }
      });
      setResolutions(initialResolutions);
    } catch (error) {
      console.error("Analysis failed:", error);
      setStep("select");
    }
  };

  const handleMerge = () => {
    onInitiateMerge(
      strategy,
      resolutions,
      strategy === "create_new_branch" ? newSessionName : undefined
    );
    handleClose();
  };

  const handleClose = () => {
    setStep("select");
    setAnalysis(null);
    setResolutions({});
    setNewSessionName("");
    onOpenChange(false);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "secondary";
    }
  };

  const getConflictTypeLabel = (type: string) => {
    switch (type) {
      case "memory_conflict": return "Memory Conflict";
      case "context_contradiction": return "Context Contradiction";
      case "duplicate_context": return "Duplicate Content";
      default: return type;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-3xl max-h-[90vh]"
        onSubmit={step === "select" ? handleAnalyze : handleMerge}
        submitDisabled={
          step === "select" 
            ? (strategy === "create_new_branch" && !newSessionName.trim())
            : false
        }
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary" />
            Merge Branches
          </DialogTitle>
          <DialogDescription>
            {step === "select" && "Select merge strategy and start analysis"}
            {step === "analyzing" && "Analyzing branches for conflicts..."}
            {step === "review" && "Review conflicts and confirm merge"}
          </DialogDescription>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-4 py-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Merging: {sourceBranchName} → {targetBranchName}</AlertTitle>
              <AlertDescription>
                AI will analyze both branches to detect conflicts in context and memories.
                You'll review and resolve any conflicts before the merge completes.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label>Merge Strategy</Label>
              <RadioGroup value={strategy} onValueChange={(v: any) => setStrategy(v)}>
                <div className="flex items-start space-x-2 rounded-lg border p-3">
                  <RadioGroupItem value="merge_into_existing" id="merge-into" />
                  <div className="flex-1">
                    <Label htmlFor="merge-into" className="font-medium cursor-pointer">
                      Bring back findings
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Merge "{sourceBranchName}" back into "{targetBranchName}". 
                      Adds new insights without creating a new branch.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-2 rounded-lg border p-3">
                  <RadioGroupItem value="create_new_branch" id="create-new" />
                  <div className="flex-1">
                    <Label htmlFor="create-new" className="font-medium cursor-pointer">
                      Combine into new branch
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create a new branch combining both. Preserves both original branches for reference.
                    </p>
                  </div>
                </div>
              </RadioGroup>

              {strategy === "create_new_branch" && (
                <div className="space-y-2 mt-3">
                  <Label htmlFor="new-name">New Branch Name</Label>
                  <Input
                    id="new-name"
                    placeholder="e.g., Combined exploration results"
                    value={newSessionName}
                    onChange={(e) => setNewSessionName(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {step === "analyzing" && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">Analyzing branches...</p>
            <p className="text-sm text-muted-foreground mt-2">
              Detecting conflicts and generating merge summary
            </p>
          </div>
        )}

        {step === "review" && analysis && (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Summary */}
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Merge Summary</AlertTitle>
                <AlertDescription>{analysis.merge_summary}</AlertDescription>
              </Alert>

              {/* Conflicts */}
              {analysis.conflicts.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Conflicts Detected</h4>
                    <Badge variant="outline">
                      {analysis.conflicts.length} {analysis.conflicts.length === 1 ? 'conflict' : 'conflicts'}
                    </Badge>
                  </div>

                  {analysis.conflicts.map((conflict, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-warning" />
                          <span className="font-medium text-sm">{getConflictTypeLabel(conflict.type)}</span>
                          <Badge variant={getSeverityColor(conflict.severity) as any} className="text-xs">
                            {conflict.severity}
                          </Badge>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground">{conflict.description}</p>

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Source:</Label>
                          <p className="p-2 bg-muted rounded text-xs">{conflict.source_item}</p>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Target:</Label>
                          <p className="p-2 bg-muted rounded text-xs">{conflict.target_item}</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs">Resolution:</Label>
                        <RadioGroup
                          value={resolutions[index]}
                          onValueChange={(v: any) => setResolutions({ ...resolutions, [index]: v })}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="source" id={`source-${index}`} />
                            <Label htmlFor={`source-${index}`} className="text-xs font-normal cursor-pointer">
                              Keep source version
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="target" id={`target-${index}`} />
                            <Label htmlFor={`target-${index}`} className="text-xs font-normal cursor-pointer">
                              Keep target version
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="both" id={`both-${index}`} />
                            <Label htmlFor={`both-${index}`} className="text-xs font-normal cursor-pointer">
                              Keep both (merge)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="neither" id={`neither-${index}`} />
                            <Label htmlFor={`neither-${index}`} className="text-xs font-normal cursor-pointer">
                              Discard both
                            </Label>
                          </div>
                        </RadioGroup>
                        <p className="text-xs text-muted-foreground italic mt-2">
                          💡 {conflict.suggestion}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Unique Insights */}
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Unique Insights: {sourceBranchName}</h4>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {analysis.unique_insights_source.map((insight, i) => (
                      <li key={i} className="flex gap-2">
                        <span>•</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Unique Insights: {targetBranchName}</h4>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {analysis.unique_insights_target.map((insight, i) => (
                      <li key={i} className="flex gap-2">
                        <span>•</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </ScrollArea>
        )}

        <DialogFooterWithShortcuts 
          onSubmit={step === "select" ? handleAnalyze : handleMerge}
        >
          <CancelButton onClick={handleClose}>
            Cancel
          </CancelButton>
          {step === "select" && (
            <SubmitButton 
              onClick={handleAnalyze}
              disabled={strategy === "create_new_branch" && !newSessionName.trim()}
            >
              <GitMerge className="h-4 w-4 mr-2" />
              Analyze Branches
            </SubmitButton>
          )}
          {step === "review" && (
            <SubmitButton onClick={handleMerge}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Complete Merge
            </SubmitButton>
          )}
        </DialogFooterWithShortcuts>
      </DialogContent>
    </Dialog>
  );
}
